// Learning Tracks query layer. Pairs with `lib/mutations/learning-tracks.ts`.
//
// Tracks are curated, ordered lists of existing resources. The list endpoint
// already gates by viewer role on the server (admins see drafts; non-admins
// only see published). The detail endpoint returns ordered items, each
// carrying the canonical `ResourceSummary` shape (the same DTO the resources
// library renders), or `resource: null` when the underlying resource is no
// longer visible to the viewer — the UI renders an archived placeholder.

import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';
import { QUERY_KEY_REGISTRY } from './normalize';

// ---- Public types ----

export type LearningTrackSummary =
  components['schemas']['LearningTrackSummary'];
export type LearningTrackItem = components['schemas']['LearningTrackItem'];
export type LearningTrackDetail =
  components['schemas']['LearningTrackDetail'];
export type LearningTrackCursorPage =
  components['schemas']['LearningTrackCursorPage'];

export type LearningTracksListParams = {
  readonly q?: string;
  /** Topic slug — only tracks containing a resource tagged with it. */
  readonly tag?: string;
  readonly pageSize?: number;
};

// ---- Query keys + leaf factories ----
//
// `all`/`lists` are invalidation prefixes (bare arrays). `list`/`detail` are
// leaves returning `infiniteQueryOptions`/`queryOptions` — key built adjacent
// to the `queryFn` from one normalized `params` object (decision 3).

export const learningTracksKeys = {
  all: ['learning-tracks'] as const,
  lists: () => [...learningTracksKeys.all, 'list'] as const,

  list: (params: LearningTracksListParams) =>
    infiniteQueryOptions({
      queryKey: [...learningTracksKeys.lists(), params] as const,
      queryFn: ({ pageParam }) =>
        fetchLearningTracks({
          ...params,
          ...(pageParam !== undefined ? { cursor: pageParam } : {}),
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    }),

  detail: (id: string) =>
    queryOptions({
      queryKey: [...learningTracksKeys.all, 'detail', id] as const,
      queryFn: () => fetchLearningTrack(id),
    }),
};

// ---- Fetchers ----

export async function fetchLearningTracks(
  params: LearningTracksListParams & { cursor?: string },
): Promise<LearningTrackCursorPage> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/learning-tracks', {
    params: {
      query: {
        ...(params.q !== undefined && params.q.length > 0
          ? { q: params.q }
          : {}),
        ...(params.tag !== undefined && params.tag.length > 0
          ? { tag: params.tag }
          : {}),
        ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
        ...(params.pageSize !== undefined
          ? { pageSize: params.pageSize }
          : {}),
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchLearningTrack(
  id: string,
): Promise<LearningTrackDetail> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/learning-tracks/{id}',
    { params: { path: { id } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data;
}

// ---- Hooks ----

export function useInfiniteLearningTracks(
  params: LearningTracksListParams,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({ ...learningTracksKeys.list(params), ...options });
}

export function useLearningTrack(
  id: string,
  options?: { enabled?: boolean; retry?: boolean | number; retryOnMount?: boolean },
) {
  return useQuery({ ...learningTracksKeys.detail(id), ...options });
}

// ---- Resource picker (uses the resources/search endpoint the rest of the
//      product picks resources through). Shaped to match the picker shape
//      `TypeaheadMultiSelect` consumes. ----

export type LearningTrackResourcePickerItem = {
  readonly id: string;
  readonly title: string;
  readonly kind: string;
  readonly publishedAt: string | null;
};

// Curation pickers (learning-track admin, topic curation) attach DRAFTS
// too, so this searches the admin listing — the member-facing /resources
// endpoints compose `readable`, which hides unpublished work. Both
// consumers are admin-only surfaces; a non-admin caller would get 403.
export async function searchResourcesForPicker(
  q: string,
): Promise<readonly LearningTrackResourcePickerItem[]> {
  const trimmed = q.trim();
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/resources', {
    params: {
      query: {
        pageSize: 20,
        // Below 2 chars the server applies no text filter — an unfiltered
        // recency page, which is what an just-opened picker should show.
        ...(trimmed.length > 0 ? { q: trimmed } : {}),
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data.items.map((r) => ({
    id: r.id,
    title: r.title,
    kind: r.kind,
    publishedAt: r.publishedAt,
  }));
}

// ---- Query-key registry (decision 6 invariant test) ----

QUERY_KEY_REGISTRY.push(
  {
    name: 'learningTracksKeys.list',
    sampleKey: learningTracksKeys.list({ q: 'leadership', pageSize: 20 })
      .queryKey,
  },
  {
    name: 'learningTracksKeys.detail',
    sampleKey: learningTracksKeys.detail('track-1').queryKey,
  },
);
